import { calculators, type entities, formatters, utilities } from "@fraction/shared";
import { isValid, parseISO } from "date-fns";

/**
 * Resolve the pending-changes serializer for a field by reusing the shared
 * field-input classifier. Keeps cents/percentage/date classification in one
 * place (`@fraction/shared` `getFieldInputProps`) so every call site stages
 * values in the shape the corresponding input expects.
 */
export const resolveSerializer = (field: string): SerializerKind => {
  const props = formatters.field.getFieldInputProps(field);
  if ("enumField" in props) return "string";
  if (props.currency || props.cents) return "cents";
  if (props.percentage) return "percentage";
  if (props.type === "date") return "date";
  return "string";
};

/**
 * Named serializers + transforms for pending-changes entries.
 *
 * These registries let pending entries hold plain JSON-serializable data — a
 * `{name, args}` pair — instead of captured closures. This keeps the zustand
 * store inspectable, debuggable via devtools, GC-friendly, and ready for
 * future features (persistence, undo, draft-restore).
 *
 * Add a new field type? Add a key here and reference it by string at the call
 * site. Custom save-time transforms go in POST_PROCESSORS; custom optimistic
 * cache updates go in CACHE_UPDATERS.
 */

export type Serializer = {
  /** Convert a stored DB value (cents, decimal, etc.) to the display string. */
  toDisplay: (val: any) => string;
  /** Convert the raw input string back to the storage value. */
  toStorage: (raw: any) => any;
};

export const SERIALIZERS = {
  string: {
    toDisplay: (v) => (v === null || v === undefined ? "" : String(v)),
    toStorage: (raw) => (raw === "" || raw === null || raw === undefined ? null : raw),
  },
  integer: {
    toDisplay: (v) => (v === null || v === undefined ? "" : String(v)),
    toStorage: (raw) => {
      if (raw === "" || raw === null || raw === undefined) return null;
      return Math.round(formatters.number.getNumberFromString(raw));
    },
  },
  /** Display-dollars input that stores as integer cents. */
  currency: {
    toDisplay: (v) => {
      if (v === null || v === undefined || v === "") return "";
      return formatters.number.getDollars(v)?.toString() ?? "";
    },
    toStorage: (raw) => {
      if (raw === "" || raw === null || raw === undefined) return null;
      return formatters.number.getCents(raw);
    },
  },
  /** Same as currency — kept as a distinct name for call-site clarity. */
  cents: {
    toDisplay: (v) => {
      if (v === null || v === undefined || v === "") return "";
      return formatters.number.getDollars(v)?.toString() ?? "";
    },
    toStorage: (raw) => {
      if (raw === "" || raw === null || raw === undefined) return null;
      return formatters.number.getCents(raw);
    },
  },
  /** Input "5.25" displays as "5.25%" and stores as decimal 0.0525. */
  percentage: {
    toDisplay: (v) => {
      if (v === null || v === undefined || v === "") return "";
      return String(Number(v) * 100);
    },
    toStorage: (raw) => {
      if (raw === "" || raw === null || raw === undefined) return null;
      return formatters.number.getNumberFromString(raw) / 100;
    },
  },
  /**
   * Checkbox/toggle fields. Staged as "true"/"" (the display strings the store
   * compares against), stored as a real boolean — sending the string "true" to a
   * boolean column relies on backend coercion that isn't guaranteed.
   *
   * "" stores as `false` rather than `null`: an untoggled flag is a decision,
   * not an absent value.
   */
  boolean: {
    toDisplay: (v) => (v ? "true" : ""),
    toStorage: (raw) => raw === "true" || raw === true,
  },

  /** ISO string in, ISO string (noon of day, UTC) out. */
  date: {
    toDisplay: (v) => {
      if (v === null || v === undefined || v === "") return "";
      if (v instanceof Date) return v.toISOString().split("T")[0];
      if (typeof v === "string") return v.split("T")[0];
      return "";
    },
    toStorage: (raw) => {
      if (raw === "" || raw === null || raw === undefined) return null;
      const d = typeof raw === "string" ? parseISO(raw) : new Date(raw);
      if (!isValid(d)) return null;
      return utilities.noonOfDay(d).toISOString();
    },
  },
} as const satisfies Record<string, Serializer>;

export type SerializerKind = keyof typeof SERIALIZERS;

/**
 * Named post-processors that run AFTER the serializer's toStorage at save time.
 * Use when the stored shape is more complex than a scalar.
 */
export const POST_PROCESSORS = {
  /**
   * Wrap a cents value into a Payment shape at the given frequency.
   *
   * Income/expense amounts live on an embedded `payment` object rather than a
   * flat column, and the frequency has to be stated explicitly: the UI displays
   * these normalised to monthly, so writing the edited number back without
   * pinning the frequency would silently reinterpret "$385/mo" as whatever
   * frequency the record happened to carry.
   *
   * `args`: `{ frequency: Frequency; existing?: object }`.
   */
  paymentAmount: (value: unknown, args?: Record<string, any>) => {
    if (value === null || value === undefined) {
      return null;
    }
    return { ...(args?.existing ?? {}), amount: value, frequency: args?.frequency };
  },
  /**
   * The input takes the STATED rate (a whole bp, compounded per the product); the column stores its
   * effective equivalent (`toStoredEffectiveRate`), which money runs on and which reads back as the
   * same stated bp. `args`: `{ periods: 12 | 365 }`.
   */
  statedRateToEffective: (value: unknown, args?: Record<string, any>) => {
    if (value === null || value === undefined || !args?.periods) {
      return value;
    }
    return calculators.rates.toStoredEffectiveRate(Number(value), args.periods).toNumber();
  },
} as const;

export type PostProcessorName = keyof typeof POST_PROCESSORS;

export const runPostProcessor = (
  name: PostProcessorName,
  storageValue: unknown,
  args?: Record<string, any>
): unknown => {
  const fn = POST_PROCESSORS[name] as (v: unknown, a?: Record<string, any>) => unknown;
  return fn(storageValue, args ?? {});
};

/**
 * Named optimistic cache updaters. Applied at save time by the save bar; they
 * compose when multiple pending entries target the same cache slice.
 */
export const CACHE_UPDATERS = {} as const;

export type CacheUpdaterName = keyof typeof CACHE_UPDATERS;

export const runCacheUpdater = (
  name: CacheUpdaterName,
  prev: entities.Property | undefined | null,
  changes: entities.Property,
  args?: Record<string, any>
): entities.Property | null | undefined => {
  const fn = CACHE_UPDATERS[name] as (
    p: entities.Property | undefined | null,
    c: entities.Property,
    a?: Record<string, any>
  ) => entities.Property | null | undefined;
  return fn(prev, changes, args);
};
